import { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import type { CalendarEvent } from '@highlight/common'
import { useGlobalStore } from '@highlight/ipc/react'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAwaitMeetingJoin } from '@renderer/hooks/useAwaitMeetingJoin'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { useCurrentMeeting } from '@renderer/hooks/useCurrentMeeting'
import { useTodaysCalendarEvents } from '@renderer/hooks/use-calendar-events'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { useMeetingMode } from '@renderer/hooks/settings/useMeetingMode'
import { useToast } from '../../use-toast'
import { useOutOfMeetingHours } from '@renderer/overlay/Sidepanel/components/meeting/use-out-of-meeting-hours'
import {
  calendarEventToScheduledMeeting,
  getIsGoogleMeet,
  getMeetingJoinLink,
} from '@shared/utils/connections/calendars'
import { joinDecidesTranscription, willTranscribeOnJoin } from '@shared/utils/meetings'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { extractGoogleMeetCode, normalizeUrl } from './utils'
import { ADD_HOURS_TO_TRANSCRIBE_CTA, useOpenHoursPaywall } from './out-of-meeting-hours'

interface UseMeetingStartingSoonToastOptions {
  meeting: CalendarEvent
  toastId: string
  force?: boolean
}

interface MeetingStartingSoonToastModel {
  meetingJoinLink: string | undefined
  transcribesOnJoin: boolean
  joinLabel: string
  transcribeLabel: string
  onJoin: () => void
  onStartTranscribing: () => void
}

/**
 * Shared lifecycle for the floating and notched starting-soon toasts. Only one
 * of those bodies is mounted, so dismiss and join behavior has to live here.
 */
export function useMeetingStartingSoonToast({
  meeting,
  toastId,
  force = false,
}: UseMeetingStartingSoonToastOptions): MeetingStartingSoonToastModel {
  const isTakingNotes = useIsTakingNotes()
  const { meetingMode } = useMeetingMode()
  const awaitMeetingJoin = useAwaitMeetingJoin()
  const { startAudioNote } = useStartAudioNote()
  const toast = useToast()
  const [, forceUpdate] = useState(0)
  const [notificationsEnabled] = useGlobalStore<boolean>('settings', 'notifications.meetingStartingSoon', true)
  const { data: calendarEvents } = useTodaysCalendarEvents()
  const { currentDetectedMeeting, currentScheduledMeeting } = useCurrentMeeting()
  const outOfHours = useOutOfMeetingHours()
  const openHoursPaywall = useOpenHoursPaywall()
  const { isIncognitoEnabled } = useIncognitoMode()

  const meetingJoinLink = getMeetingJoinLink(meeting)
  const isGoogleMeet = getIsGoogleMeet(meeting)
  const transcribesOnJoin = willTranscribeOnJoin({
    mode: meetingMode,
    hasJoinLink: !!meetingJoinLink,
    incognito: isIncognitoEnabled,
    outOfHours,
  })

  useEffect(() => {
    if (currentScheduledMeeting?.id === meeting.id) {
      toast.dismiss(toastId)
      return
    }

    if (isTakingNotes) {
      toast.dismiss(toastId)
      return
    }

    if (currentDetectedMeeting) {
      if (currentDetectedMeeting.url && meeting.conferenceData?.uri) {
        const normalizedDetectedUrl = normalizeUrl(currentDetectedMeeting.url)
        const normalizedScheduledUrl = normalizeUrl(meeting.conferenceData.uri)

        if (normalizedDetectedUrl === normalizedScheduledUrl) {
          toast.dismiss(toastId)
          return
        }

        if (isGoogleMeet) {
          const detectedCode = extractGoogleMeetCode(currentDetectedMeeting.url)
          const scheduledCode = extractGoogleMeetCode(meeting.conferenceData.uri)
          if (detectedCode && scheduledCode && detectedCode === scheduledCode) {
            toast.dismiss(toastId)
            return
          }
        }
      }

      if (currentDetectedMeeting.windowTitle && meeting.title) {
        const cleanTitle = currentDetectedMeeting.windowTitle
          .toLowerCase()
          .replace(/^meet [-–] /i, '')
          .trim()
        const eventTitle = meeting.title.toLowerCase()

        if (cleanTitle.includes(eventTitle) || eventTitle.includes(cleanTitle)) {
          toast.dismiss(toastId)
          return
        }
      }
    }
  }, [currentDetectedMeeting, currentScheduledMeeting?.id, meeting, toast, toastId, isGoogleMeet, isTakingNotes])

  useEffect(() => {
    if (!force && !notificationsEnabled) {
      toast.dismiss(toastId)
    }
  }, [force, notificationsEnabled, toast, toastId])

  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate((prev) => prev + 1)

      const now = Date.now()
      const meetingEnd = meeting.end
        ? new Date(meeting.end).getTime()
        : new Date(meeting.start).getTime() + 60 * 60 * 1000

      if (now > meetingEnd + 5 * 60 * 1000) {
        toast.dismiss(toastId)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [meeting.start, meeting.end, toast, toastId])

  const wasInCalendarRef = useRef(false)
  useEffect(() => {
    if (currentScheduledMeeting && currentScheduledMeeting.id === meeting.id) {
      toast.dismiss(toastId)
      return
    }

    if (!force && calendarEvents !== undefined) {
      const isInCalendar = calendarEvents.some((event) => event.id === meeting.id)
      if (isInCalendar) {
        wasInCalendarRef.current = true
      } else if (wasInCalendarRef.current) {
        toast.dismiss(toastId)
      }
    }
  }, [force, calendarEvents, currentScheduledMeeting, meeting.id, toast, toastId])

  const onJoinScheduledMeeting = useCallback(async () => {
    if (!meetingJoinLink) {
      return
    }

    // Armed before the link opens: arming saves any note running for another
    // meeting, which must finish before this call can be detected.
    await awaitMeetingJoin({ source: 'toast', meeting })
    try {
      const url = new URL(meetingJoinLink)
      if (meeting.userEmail && isGoogleMeet) {
        url.searchParams.set('authuser', meeting.userEmail ?? '')
      }
      const urlString = url.toString() || meetingJoinLink
      if (urlString) {
        window.open(urlString, '_blank', 'noopener,noreferrer')
      }
    } catch {
      if (meetingJoinLink) {
        window.open(meetingJoinLink, '_blank', 'noopener,noreferrer')
      }
    }
    // The await stays armed out of hours so hours bought before the meeting is
    // detected still start its transcription.
    if (outOfHours) {
      openHoursPaywall(meeting)
    }
    AmplitudeRenderer.trackEvent('Audio Note Action', {
      action: 'join scheduled meeting',
    })
  }, [awaitMeetingJoin, meetingJoinLink, isGoogleMeet, meeting, outOfHours, openHoursPaywall])

  const onJoin = useCallback(() => {
    void onJoinScheduledMeeting()
      .then(() => {
        toast.dismiss(toastId)
      })
      .catch((error: unknown) => {
        Sentry.captureException(error, { tags: { module: 'meeting-starting-soon-toast' } })
      })
  }, [onJoinScheduledMeeting, toast, toastId])

  const onStartTranscribing = useCallback(() => {
    if (outOfHours) {
      openHoursPaywall(meeting)
    } else {
      startAudioNote({ source: 'toast', meeting: calendarEventToScheduledMeeting(meeting) })
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start taking notes',
        type: 'scheduled meeting without link',
      })
    }
    toast.dismiss(toastId)
  }, [outOfHours, openHoursPaywall, startAudioNote, meeting, toast, toastId])

  const joinLabel = joinDecidesTranscription({ mode: meetingMode, incognito: isIncognitoEnabled, outOfHours })
    ? 'Join & Transcribe'
    : 'Join Meeting'
  const transcribeLabel = outOfHours ? ADD_HOURS_TO_TRANSCRIBE_CTA : 'Transcribe'

  return {
    meetingJoinLink,
    transcribesOnJoin,
    joinLabel,
    transcribeLabel,
    onJoin,
    onStartTranscribing,
  }
}
